/**
 * Atom: TopBar
 *
 * Phase A · MOBILE-RESTYLE-PHASE-A · Spec §06.5 (.t2-mob-top delta).
 *
 * Sticky mobile top bar with three slots:
 *   - `left`     — back button / project switcher / brand mark
 *   - children   — optional center content (rare; usually the left slot
 *                  carries the title)
 *   - `right`    — overflow / icon buttons (Spec P-03 "⋯ top-right is
 *                  universal" lives here)
 *
 * Geometry comes from the spec's `.t2-mob-top` rule (10px 16px 6px,
 * gap 10). Theme colors come from CSS vars so light + dark inherit the
 * same atom. Safe-area-inset-top is folded into the sticky offset so
 * the bar clears the iOS notch without callers needing to know.
 *
 *   <TopBar
 *     left={<MobileProjectSwitcher />}
 *     right={<IconButton aria-label="More" onClick={open}><Menu/></IconButton>}
 *   />
 */
import React from 'react'

export interface TopBarProps {
  left?: React.ReactNode
  right?: React.ReactNode
  children?: React.ReactNode
  /** Default true. Set false for surfaces that scroll the bar away (rare). */
  sticky?: boolean
  style?: React.CSSProperties
}

export const TopBar: React.FC<TopBarProps> = ({
  left,
  right,
  children,
  sticky = true,
  style,
}) => {
  return (
    <div
      style={{
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        zIndex: 100,
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)',
        // Spec §06.5: 10px top / 16px sides / 6px bottom, gap 10.
        // Add safe-area-inset-top so iOS notch clears.
        padding: '10px 16px 6px',
        paddingTop: 'calc(10px + env(safe-area-inset-top))',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        ...style,
      }}
    >
      {left && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          {left}
        </div>
      )}
      {children}
      {right && (
        <div
          style={{
            marginLeft: left ? 0 : 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          {right}
        </div>
      )}
    </div>
  )
}

export default TopBar
